import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import type { ProcessedEntry, SortField, SortDirection, InvoiceLine } from '../../types/financialReport';
import { ExpandToggle } from '../ui/ListRows';

interface FinancialTableProps {
  entries: ProcessedEntry[];
  title: string;
  subtitle: string;
  showClientInvoice?: boolean;
  showInfluencerInvoice?: boolean;
  loading: boolean;
}

function formatDateDisplay(value: string): string {
  if (!value) return '--';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-GB');
}

function InvoiceSubRow({ line }: { line: InvoiceLine }) {
  return (
    <tr className="bg-gray-50/40 text-xs">
      <td className="pl-10 pr-3 py-2 text-gray-500 font-mono">{line.invoice_number || '--'}</td>
      <td className="px-3 py-2 text-gray-500">
        {line.purchase_order ? `PO: ${line.purchase_order}` : ''}
      </td>
      <td className="px-3 py-2 text-gray-500">
        {line.issued_date ? `Issued ${formatDateDisplay(line.issued_date)}` : ''}
      </td>
      <td className="px-3 py-2 text-gray-500">
        {line.due_date ? `Due ${formatDateDisplay(line.due_date)}` : '--'}
      </td>
      <td className="px-3 py-2 text-gray-700 font-medium">{formatEuro(line.amount)}</td>
      <td className="px-3 py-2 text-gray-700">{line.amount_paid > 0 ? formatEuro(line.amount_paid) : '--'}</td>
      <td className="px-3 py-2 text-gray-700">{line.balance > 0 ? formatEuro(line.balance) : '--'}</td>
      <td className="px-3 py-2">
        <PaymentStatusBadge
          category={line.category}
          rawStatus={line.status_pt || line.status_en}
          daysOverdue={line.days_overdue}
        />
      </td>
      <td className="px-3 py-2 text-gray-500">
        {line.payment_date ? formatDateDisplay(line.payment_date) : '--'}
      </td>
    </tr>
  );
}

export function FinancialTable({
  entries,
  title,
  subtitle,
  showClientInvoice = true,
  showInfluencerInvoice = true,
  loading,
}: FinancialTableProps) {
  const [sortField, setSortField] = useState<SortField>('work_code');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...entries].sort((a, b) => {
    const mult = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'work_code':
        return mult * a.work_code.localeCompare(b.work_code, undefined, { numeric: true });
      case 'brand':
        return mult * a.brand.localeCompare(b.brand);
      case 'fee':
        return mult * (a.fee - b.fee);
      case 'client_invoice_amount':
        return mult * (a.client_billed - b.client_billed);
      case 'influencer_invoice_amount':
        return mult * (a.supplier_billed - b.supplier_billed);
      case 'client_balance_due':
        return mult * (a.client_balance_due - b.client_balance_due);
      case 'supplier_balance_due':
        return mult * (a.supplier_balance_due - b.supplier_balance_due);
      case 'campaign_status': {
        const order: Record<string, number> = { Completed: 4, Billing: 3, 'In Progress': 2, Consultation: 1 };
        return mult * ((order[a.campaign_status] || 0) - (order[b.campaign_status] || 0));
      }
      default:
        return 0;
    }
  });

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-900 select-none group"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">
          {sortField === field ? (
            sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3 text-gray-300" />
          )}
        </span>
      </div>
    </th>
  );

  const totals = sorted.reduce(
    (acc, e) => ({
      fee: acc.fee + e.fee,
      commission: acc.commission + e.commission_amount,
      clientBilled: acc.clientBilled + e.client_billed,
      clientReceived: acc.clientReceived + e.client_received,
      clientBalance: acc.clientBalance + e.client_balance_due,
      supplierBilled: acc.supplierBilled + e.supplier_billed,
      supplierPaid: acc.supplierPaid + e.supplier_paid,
      supplierBalance: acc.supplierBalance + e.supplier_balance_due,
    }),
    {
      fee: 0,
      commission: 0,
      clientBilled: 0,
      clientReceived: 0,
      clientBalance: 0,
      supplierBilled: 0,
      supplierPaid: 0,
      supplierBalance: 0,
    }
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="h-5 w-48 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-4 w-64 bg-gray-100 rounded animate-pulse mb-6" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-10 w-full bg-gray-50 rounded animate-pulse mb-2" />
        ))}
      </div>
    );
  }

  const colSpan =
    6 +
    (showClientInvoice ? 5 : 0) +
    (showInfluencerInvoice ? 5 : 0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle} -- {sorted.length} entries</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50/80">
            <tr>
              <th className="w-8 px-2 py-3" />
              <SortHeader field="work_code">Code</SortHeader>
              <SortHeader field="brand">Brand</SortHeader>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Influencer(s)</th>
              <SortHeader field="fee">Fee</SortHeader>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Comm.</th>
              <SortHeader field="campaign_status">Status</SortHeader>
              {showClientInvoice && (
                <>
                  <SortHeader field="client_invoice_amount">Client Billed</SortHeader>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Client Received</th>
                  <SortHeader field="client_balance_due">Client Balance</SortHeader>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Client Pay Status</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Next Due</th>
                </>
              )}
              {showInfluencerInvoice && (
                <>
                  <SortHeader field="influencer_invoice_amount">Inf. Billed</SortHeader>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Inf. Paid</th>
                  <SortHeader field="supplier_balance_due">Inf. Balance</SortHeader>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Inf. Pay Status</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Latest Payment</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sorted.map((entry) => {
              const isOpen = expanded.has(entry.id);
              const hasInvoices = entry.client_invoices.length + entry.supplier_invoices.length > 0;
              const latestClientPayment = entry.client_invoices
                .map(l => l.payment_date)
                .filter(Boolean)
                .sort()
                .pop();
              const latestSupplierPayment = entry.supplier_invoices
                .map(l => l.payment_date)
                .filter(Boolean)
                .sort()
                .pop();
              return (
                <>
                  <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-2 py-2.5">
                      {hasInvoices ? (
                        <ExpandToggle
                          open={isOpen}
                          onClick={() => toggleExpanded(entry.id)}
                          label={isOpen ? 'Fechar faturas' : 'Ver faturas'}
                        />
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono font-medium text-gray-900 whitespace-nowrap">{entry.work_code}</td>
                    <td className="px-3 py-2.5 text-sm text-gray-700 max-w-[160px] truncate" title={`${entry.brand} (${entry.client})`}>
                      {entry.brand}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-600 max-w-[180px] truncate" title={entry.influencers}>
                      {entry.influencers || '--'}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">{formatEuro(entry.fee)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {entry.commission_pct > 0 ? `${entry.commission_pct.toFixed(0)}%` : '--'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <CampaignStatusBadge status={entry.campaign_status} />
                    </td>
                    {showClientInvoice && (
                      <>
                        <td className="px-3 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {entry.client_billed > 0 ? formatEuro(entry.client_billed) : '--'}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                          {entry.client_received > 0 ? formatEuro(entry.client_received) : '--'}
                        </td>
                        <td className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap ${entry.client_balance_due > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                          {entry.client_balance_due > 0 ? formatEuro(entry.client_balance_due) : '--'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <PaymentStatusBadge
                            category={entry.client_payment_category}
                            rawStatus={entry.client_invoice_status}
                            daysOverdue={entry.max_client_days_overdue}
                          />
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {entry.next_client_due_date ? formatDateDisplay(entry.next_client_due_date) : '--'}
                        </td>
                      </>
                    )}
                    {showInfluencerInvoice && (
                      <>
                        <td className="px-3 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {entry.supplier_billed > 0 ? formatEuro(entry.supplier_billed) : '--'}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-gray-700 whitespace-nowrap">
                          {entry.supplier_paid > 0 ? formatEuro(entry.supplier_paid) : '--'}
                        </td>
                        <td className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap ${entry.supplier_balance_due > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
                          {entry.supplier_balance_due > 0 ? formatEuro(entry.supplier_balance_due) : '--'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <PaymentStatusBadge
                            category={entry.influencer_payment_category}
                            rawStatus={entry.influencer_invoice_status}
                            daysOverdue={entry.max_supplier_days_overdue}
                          />
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                          {latestSupplierPayment ? formatDateDisplay(latestSupplierPayment) : latestClientPayment && !showClientInvoice ? '--' : '--'}
                        </td>
                      </>
                    )}
                  </tr>
                  {isOpen && hasInvoices && (
                    <>
                      {showClientInvoice && entry.client_invoices.length > 0 && (
                        <tr className="bg-gray-50/40">
                          <td colSpan={colSpan + 1} className="px-10 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                            Client invoices (Mohit to Client)
                          </td>
                        </tr>
                      )}
                      {showClientInvoice && entry.client_invoices.map((line, idx) => (
                        <InvoiceSubRow key={`${entry.id}-c-${idx}`} line={line} />
                      ))}
                      {showInfluencerInvoice && entry.supplier_invoices.length > 0 && (
                        <tr className="bg-gray-50/40">
                          <td colSpan={colSpan + 1} className="px-10 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                            Supplier invoices (Creator to Mohit)
                          </td>
                        </tr>
                      )}
                      {showInfluencerInvoice && entry.supplier_invoices.map((line, idx) => (
                        <InvoiceSubRow key={`${entry.id}-s-${idx}`} line={line} />
                      ))}
                    </>
                  )}
                </>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr className="font-semibold">
              <td className="px-2 py-3" />
              <td className="px-3 py-3 text-sm text-gray-900" colSpan={3}>TOTALS</td>
              <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatEuro(totals.fee)}</td>
              <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{formatEuro(totals.commission)}</td>
              <td className="px-3 py-3" />
              {showClientInvoice && (
                <>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatEuro(totals.clientBilled)}</td>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatEuro(totals.clientReceived)}</td>
                  <td className="px-3 py-3 text-sm text-amber-700 whitespace-nowrap">{formatEuro(totals.clientBalance)}</td>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3" />
                </>
              )}
              {showInfluencerInvoice && (
                <>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatEuro(totals.supplierBilled)}</td>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">{formatEuro(totals.supplierPaid)}</td>
                  <td className="px-3 py-3 text-sm text-amber-700 whitespace-nowrap">{formatEuro(totals.supplierBalance)}</td>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3" />
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
